// /albums/trash — the soft-deleted albums (W39.61).
//
// The third screen of its kind, and deliberately the same shape as
// `/wishes/trash` and `/anniversaries/trash`: `TrashList` is shared, and the two
// differences that matter — an album is `name` where a wish is also `name` and an
// anniversary is `title`, and the photos went to 「未分類」 at delete time rather
// than coming back with the album — are handled in the copy below rather than
// pushed into the shared component.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { TrashList, type TrashRow } from "@/components/system/TrashList";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { listAlbumTrash, purgeAlbum, restoreAlbum } from "@/lib/api/album";
import { queryKeys } from "@/lib/queries/keys";
import { ApiError } from "@/lib/api/pair";
import { appErrorMessage } from "@/lib/i18n/app";
import { ALBUM_RESTORE_DAYS } from "@/lib/retentionWindows";

const TRASH_KEY = ["albums", "trash"] as const;

export function AlbumTrashPage() {
  const qc = useQueryClient();

  const trash = useQuery({ queryKey: TRASH_KEY, queryFn: listAlbumTrash });

  const afterChange = () => {
    void qc.invalidateQueries({ queryKey: TRASH_KEY });
    // The live list, and each album's own detail, both have a count in them.
    void qc.invalidateQueries({ queryKey: queryKeys.album.list() });
    void qc.invalidateQueries({ queryKey: queryKeys.album.all });
  };

  const restoreMut = useMutation({ mutationFn: restoreAlbum, onSuccess: afterChange });
  const purgeMut = useMutation({ mutationFn: purgeAlbum, onSuccess: afterChange });

  const rows: TrashRow[] = (trash.data ?? []).map((a) => ({
    id: a.id,
    label: a.name,
    daysLeft: a.daysLeft,
    canRestore: a.canRestore,
  }));

  return (
    <>
      <TrashList
        noun="相簿垃圾桶"
        itemNoun="相簿"
        parentTo="/albums"
        parentLabel="相簿"
        retentionDays={ALBUM_RESTORE_DAYS}
        rows={rows}
        isLoading={trash.isLoading}
        isError={trash.isError}
        errorMessage={
          trash.error instanceof ApiError
            ? appErrorMessage(trash.error.code, trash.error.message)
            : "載入相簿垃圾桶失敗，請稍後再試。"
        }
        onRetry={() => void trash.refetch()}
        onRestore={async (id) => {
          await restoreMut.mutateAsync(id);
        }}
        onPurge={async (id) => {
          await purgeMut.mutateAsync(id);
        }}
      />
      <p className="px-s-4 pb-s-4 text-body-sm text-ink-3">
        {/* Worth saying here and nowhere else: deleting an album moves its photos
            to 「未分類」 rather than deleting them (ADR-0045), so 還原 brings back
            the *album* and the pictures are already in the system album. Saying
            nothing would let someone restore, find the grid empty, and conclude
            the restore failed.

            The way back is the breadcrumb in the header now (W45.0 `ADR-0115`),
            so the link that used to trail this sentence is gone. */}
        刪除相簿時, 裡面的照片會移到「未分類」, 照片本身不會消失。
      </p>
    </>
  );
}

export function AlbumTrashRoute() {
  return (
    <PageErrorBoundary label="相簿垃圾桶">
      <AlbumTrashPage />
    </PageErrorBoundary>
  );
}
